import React from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import ReCAPTCHA from 'react-google-recaptcha'
import ReactPixel from 'react-facebook-pixel'

import { ContentBottom, ContentForm, Spacing, Button } from './styles'
import { contactUs } from 'src/api/authOtp'
import { IPropUpdateInfoUser } from 'types/form'
import {
    FiltForm,
    FiltFormSelect,
    FiltFormTextArea,
    TermsAndConditions,
} from 'src/components/common/Form'
import { ServicesLabels } from 'src/constants'
import { SubscribeProps } from 'pages/BookACall/ContactForm'
import { useTranslations, useI18nInstance } from 'src/provider/LocaleProvider'
import { Props } from './type'

export const FormData = ({
    setCongratulations,
    setError,
    setEmailError,
    width,
}: SubscribeProps) => {
    const { ContentOltServices } = ServicesLabels()
    const t = useTranslations()
    const i18n = useI18nInstance()
    const [isCaptchaSuccessful, setIsCaptchaSuccess] = React.useState(false)
    const validationSchema = Yup.object().shape({
        name: Yup.string()
            .required(
                i18n.language === 'es'
                    ? 'Nombre es requerido'
                    : 'Name is required'
            )
            .min(
                3,
                i18n.language === 'es' ? 'Nombre muy corto' : 'Very short name'
            )
            .max(
                30,
                i18n.language === 'es' ? 'Nombre muy largo' : 'Very long name'
            ),

        email: Yup.string()
            .email(
                i18n.language === 'es'
                    ? 'Correo electrónico no válido'
                    : 'Invalid e-mail address'
            )
            .required(
                i18n.language === 'es'
                    ? 'Debe introducir la dirección de correo electrónico de su empresa'
                    : "You must enter your company's e-mail address."
            ),
        terms_and_conditions: Yup.boolean().oneOf(
            [true],
            i18n.language === 'es'
                ? 'Debe aceptar las condiciones'
                : 'You must accept the terms and conditions'
        ),
        Captcha: Yup.boolean().test('true', 'false', () => {
            return isCaptchaSuccessful
        }),
    })
    const formik = useFormik({
        initialValues: {
            name: '',
            email: '',
            service_of_interest: '',
            message: '',
            terms_and_conditions: false,
            continue_receiving_information: false,
        },
        validationSchema: validationSchema,
        onSubmit: (values, { resetForm }) =>
            handleSubmitForm({ values, resetForm }),
    })
    async function handleSubmitForm({ values, resetForm }: Props) {
        const data: IPropUpdateInfoUser = values.continue_receiving_information
            ? {
                  ...values,
                  terms_and_conditions: new Date(),
                  continue_receiving_information: new Date(),
              }
            : {
                  ...values,
                  terms_and_conditions: new Date(),
                  continue_receiving_information: null,
              }
        try {
            await contactUs(data)
            resetForm()
            setCongratulations(true)
            ReactPixel.track('Schedule a Call', {
                name: values.name,
                email: values.email,
                service_of_interest: values.service_of_interest,
                message: values.message,
            })
            if (window.dataLayer) {
                window.dataLayer.push({ event: 'Schedule a Call' })
            }
        } catch (e) {
            const error = e as any
            if (error.response.data.statusCode === 406) {
                setEmailError(true)
                return
            }
            setError(true)
            console.error(error)
        }
    }

    function robotVerification(value: any) {
        setIsCaptchaSuccess(true)
        console.log('captcha value: ', value)
    }

    return (
        <ContentForm onSubmit={formik.handleSubmit} width={width}>
            <Spacing marginBottom={'xxmedium'}>
                <FiltForm
                    id={'name'}
                    type={'text'}
                    name={'name'}
                    errors={formik.errors.name}
                    touched={formik.touched.name}
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    title={t('fields.name.label')}
                />
            </Spacing>
            <Spacing marginBottom={'medium'}>
                <FiltForm
                    id={'email'}
                    name={'email'}
                    type={'email'}
                    errors={formik.errors.email}
                    touched={formik.touched.email}
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    title={t('fields.email.label')}
                />
            </Spacing>
            <Spacing marginBottom={'xxmedium'}>
                <FiltFormSelect
                    id={'service_of_interest'}
                    name={'service_of_interest'}
                    errors={formik.errors.service_of_interest}
                    touched={formik.touched.service_of_interest}
                    value={formik.values.service_of_interest}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    title={t('fields.service_of_interest.label')}
                    dataSelect={ContentOltServices}
                />
            </Spacing>
            <Spacing marginBottom={'xxmedium'}>
                <FiltFormTextArea
                    id={'message'}
                    name={'message'}
                    label={
                        t('BookACall.form.message') ?? 'Default Message Label'
                    }
                    errors={formik.errors.message}
                    touched={formik.touched.message}
                    value={formik.values.message}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    title={t('fields.message.label') ?? 'Default Message Title'}
                />
            </Spacing>
            <Spacing marginBottom={'xxmedium'}>
                <TermsAndConditions
                    touched={formik.touched.terms_and_conditions}
                    errors={formik.errors.terms_and_conditions}
                    values={formik.values.terms_and_conditions}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    Content={t('bookACall.terms_and_conditions')}
                    data={'terms_and_conditions'}
                />
            </Spacing>
            <Spacing marginBottom={'xxmedium'}>
                <TermsAndConditions
                    touched={formik.touched.continue_receiving_information}
                    errors={formik.errors.continue_receiving_information}
                    values={formik.values.continue_receiving_information}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    Content={t('bookACall.continue_receiving_information')}
                    data={'continue_receiving_information'}
                />
            </Spacing>
            <Spacing marginBottom={'xxmedium'}>
                <ReCAPTCHA
                    sitekey="6Ld5a70lAAAAAHP144QIet0EDlN017OJmyKYe2rS"
                    onChange={robotVerification}
                ></ReCAPTCHA>
            </Spacing>
            <Spacing marginBottom={'xxmedium'}>
                <ContentBottom>
                    <Button
                        enable={
                            Object.keys(formik.errors).length !== 0 &&
                            !isCaptchaSuccessful
                        }
                        type="submit"
                    >
                        {t('cta.sendMessage')}
                    </Button>
                </ContentBottom>
            </Spacing>
        </ContentForm>
    )
}
